import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Users, Star, ChevronLeft, ChevronRight, Shield, Heart, Share2, Play, Globe, HelpCircle, BedDouble, Coffee } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { useExperiencesStore } from '@/lib/stores/experiences-store';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { LocationMap } from '@/components/experiences/LocationMap';
import { AttendeesList } from '@/components/experiences/AttendeesList';
import { formatDuration } from '@/lib/utils/duration';
import { BookingButton } from '@/components/experiences/BookingButton';
import { AuthModal } from '@/components/auth/AuthModal';
import { PostPurchaseShareModal } from '@/components/experiences/PostPurchaseShareModal';
import { useStripeCheckoutStatus } from '@/hooks/useStripeCheckoutStatus';
import { useUserPurchaseStatus } from '@/hooks/useUserPurchaseStatus';
import { useModalStore } from '@/lib/stores/modal-store';

export function ExperienceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUnifiedAuth();
  const { openAuthModal } = useModalStore();
  const { experiences } = useExperiencesStore();
  const [currentMediaIndex, setCurrentMediaIndex] = React.useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = React.useState(false);
  const [showShareModal, setShowShareModal] = React.useState(false);

  const experience = experiences.find(exp => exp.id === id);
  const { hasPurchased, isLoading: purchaseLoading } = useUserPurchaseStatus(id || '');

  // Handle post-purchase sharing
  useStripeCheckoutStatus({
    onSuccess: () => {
      // Show share modal after successful purchase
      setTimeout(() => setShowShareModal(true), 1000);
    }
  });

  if (!experience) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-sand-400">Experience not found</p>
      </div>
    );
  }

  // Ensure media is an array and sort it by order_index
  const sortedMedia = experience.media && Array.isArray(experience.media) 
    ? [...experience.media].sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
    : [];

  // Default image if no media is available
  const defaultImage = 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&q=80';

  // Get the current media item
  const currentMedia = sortedMedia[currentMediaIndex] || { url: defaultImage, type: 'image' };

  // Sample FAQ data
  const faqs = [
    {
      question: "What should I bring?",
      answer: "Comfortable clothing, a camera, and an open mind. All other necessary equipment will be provided."
    },
    {
      question: "Is transportation included?",
      answer: "Yes, private transportation from the meeting point to all locations is included."
    },
    {
      question: "What's the cancellation policy?",
      answer: "Full refund up to 7 days before the experience. 50% refund up to 48 hours before."
    }
  ];

  // Media for different tabs
  const tabMedia = {
    about: sortedMedia[1] || sortedMedia[0],
    location: sortedMedia[2] || sortedMedia[0],
    accommodation: sortedMedia[3] || sortedMedia[0]
  };

  return (
    <div className="min-h-screen bg-earth-900">
      {/* Hero Media */}
      <div className="relative h-[100vh]">
        {currentMedia.type === 'video' ? (
          <video
            src={currentMedia.url}
            className="w-full h-full object-cover"
            controls={isVideoPlaying}
            onClick={() => setIsVideoPlaying(!isVideoPlaying)}
            poster={sortedMedia.find(m => m.type === 'image')?.url}
          />
        ) : (
          <img
            src={currentMedia.url}
            alt={experience.title}
            className="w-full h-full object-cover"
          />
        )}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-x-0 top-30 bottom-0 bg-gradient-to-t from-earth-900 via-earth-900/50 to-transparent" />
        
        {/* Thumbnails */}
        {sortedMedia.length > 1 && (
          <div className="absolute bottom-4 left-4 right-4 flex gap-2 overflow-x-auto scrollbar-hide">
            {sortedMedia.map((media, index) => (
              <button
                key={index}
                onClick={() => setCurrentMediaIndex(index)}
                className={`relative h-16 w-24 rounded-lg overflow-hidden flex-shrink-0 border-2 
                  ${currentMediaIndex === index ? 'border-sand-300' : 'border-transparent'}`}
              >
                {media.type === 'video' ? (
                  <div className="relative w-full h-full">
                    <video
                      src={media.url}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-earth-900/50">
                      <Play className="h-6 w-6 text-sand-300" />
                    </div>
                  </div>
                ) : (
                  <img
                    src={media.url}
                    alt={`View ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}
              </button>
            ))}
          </div>
        )}

        {/* Experience Details (Sticky to Hero) */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-earth-900 to-transparent">
          <div className="container mx-auto px-4 py-8">
            <h1 className="text-4xl md:text-5xl font-display mb-6">{experience.title}</h1>
            <div className="flex flex-wrap gap-6 text-sand-300">
              {experience.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  <span>{experience.location.name}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>{formatDuration(experience.duration)}</span>
              </div>
              {Array.isArray(experience.languages) && experience.languages.length > 0 && (
                <div className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  <span>{experience.languages.join(', ')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Creator Info */}
            <div className="bg-earth-800/50 p-6 rounded-lg">
              <div className="flex items-center gap-4">
                <img
                  src={experience.creator?.profile_image || `https://api.dicebear.com/7.x/initials/svg?seed=${experience.creator?.full_name}&backgroundColor=1d1918&textColor=e8e4dc`}
                  alt={experience.creator?.full_name}
                  className="w-16 h-16 rounded-lg object-cover bg-earth-800"
                />
                <div>
                  <h3 className="text-xl font-display mb-1">
                    Hosted by {experience.creator?.full_name}
                  </h3>
                  <div className="flex items-center gap-1 text-green-400">
                    <Shield className="h-4 w-4" />
                    <span>Verified Host</span>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="ml-auto"
                  onClick={() => navigate(`/creators/${experience.creator?.id}`)}
                >
                  View Profile
                </Button>
              </div>
            </div>

           
            {/* Tabbed Content */}
            <div className="bg-earth-800/50 rounded-lg overflow-hidden">
              <Tabs defaultValue="about" className="w-full">
                <TabsList className="w-full border-b border-earth-700">
                  <TabsTrigger value="about" className="flex-1">About</TabsTrigger>
                  <TabsTrigger value="location" className="flex-1">Location</TabsTrigger>
                  <TabsTrigger value="accommodation" className="flex-1">Accommodation</TabsTrigger>
                  <TabsTrigger value="included" className="flex-1">What's Included</TabsTrigger>
                  <TabsTrigger value="faq" className="flex-1">FAQ</TabsTrigger>
                </TabsList>

                <TabsContent value="about" className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="experience-description">
                      {experience.description.split('\n').map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden">
                      {tabMedia.about?.type === 'video' ? (
                        <video
                          src={tabMedia.about.url}
                          className="w-full h-full object-cover"
                          controls
                          playsInline
                        />
                      ) : (
                        <img 
                          src={tabMedia.about?.url || defaultImage}
                          alt="Experience highlight"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="location" className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-xl font-display mb-4">Location Details</h3>
                      <p className="text-sand-300 mb-4">
                        Experience the magic of this carefully chosen location, selected for its unique charm and authentic character.
                      </p>
                      {experience.location && <LocationMap location={experience.location} />}
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden">
                      {tabMedia.location?.type === 'video' ? (
                        <video
                          src={tabMedia.location.url}
                          className="w-full h-full object-cover"
                          controls
                          playsInline
                        />
                      ) : (
                        <img 
                          src={tabMedia.location?.url || defaultImage}
                          alt="Location highlight"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="accommodation" className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-xl font-display mb-4">Your Stay</h3>
                      <p className="text-sand-300 mb-4">
                        Immerse yourself in carefully curated accommodations that perfectly complement your experience. 
                        Each space has been selected to ensure comfort, authenticity, and an unforgettable stay.
                      </p>
                      <ul className="space-y-4 text-sand-300">
                        <li className="flex items-start gap-2">
                          <BedDouble className="h-5 w-5 flex-shrink-0 mt-1" />
                          <span>Luxurious rooms with premium amenities and stunning views</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Coffee className="h-5 w-5 flex-shrink-0 mt-1" />
                          <span>Daily breakfast and select meals included</span>
                        </li>
                      </ul>
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden">
                      {tabMedia.accommodation?.type === 'video' ? (
                        <video
                          src={tabMedia.accommodation.url}
                          className="w-full h-full object-cover"
                          controls
                          playsInline
                        />
                      ) : (
                        <img 
                          src={tabMedia.accommodation?.url || defaultImage}
                          alt="Accommodation highlight"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="included" className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {Array.isArray(experience.included_items) && experience.included_items.length > 0 && (
                      <div>
                        <h3 className="text-xl font-display mb-4">What's included</h3>
                        <ul className="list-disc list-inside text-sand-300 space-y-2">
                          {experience.included_items.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {Array.isArray(experience.not_included_items) && experience.not_included_items.length > 0 && (
                      <div>
                        <h3 className="text-xl font-display mb-4">Not included</h3>
                        <ul className="list-disc list-inside text-sand-300 space-y-2">
                          {experience.not_included_items.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="faq" className="p-8">
                  <div className="space-y-6">
                    <h3 className="text-xl font-display mb-6">Frequently Asked Questions</h3>
                    {faqs.map((faq, index) => (
                      <div key={index} className="space-y-2">
                        <div className="flex items-start gap-2">
                          <HelpCircle className="h-5 w-5 text-sand-400 flex-shrink-0 mt-1" />
                          <h4 className="text-lg font-medium text-sand-100">{faq.question}</h4>
                        </div>
                        <p className="text-sand-300 ml-7">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="sticky top-24 space-y-6">
              <div className="bg-earth-800/50 p-6 rounded-lg">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <span className="text-3xl font-display">${experience.price}</span>
                    <span className="text-sand-400"> per person</span>
                  </div>
                  <Button variant="outline" size="sm">
                    <Heart className="h-4 w-4" />
                  </Button>
                </div>

                {user ? (
                  hasPurchased ? (
                    <Button 
                      className="w-full bg-sand-200 text-earth-900 hover:bg-sand-100"
                      onClick={() => setShowShareModal(true)}
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Bring Your Friends
                    </Button>
                  ) : (
                    <BookingButton
                      experience={experience}
                      quantity={1}
                      date=""
                      className="w-full"
                    />
                  )
                ) : (
                  <Button 
                    className="w-full"
                    onClick={() => openAuthModal('sign-up')}
                  >
                    Sign in to {experience.booking_type === 'instant' ? 'Book' : 'Request'}
                  </Button>
                )}

                <div className="mt-6 p-4 bg-earth-800 rounded-lg">
                  <div className="flex items-center gap-2 text-sand-400">
                    <Shield className="h-5 w-5" />
                    <span className="text-sm">
                      {experience.booking_type === 'instant' ? 
                        'Secure instant booking powered by Stripe' :
                        'Your request will be reviewed by the host'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Add AttendeesList component */}
              <AttendeesList experienceId={experience.id} />
            </div>
          </div>
        </div>
      </div>

      {/* Add spacing before footer */}
      <div className="h-24"></div>

      <PostPurchaseShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        experienceTitle={experience.title}
        experienceUrl={window.location.href}
        experienceImage={currentMedia.url}
      />
    </div>
  );
}